import {
  fetchDatabaseAiTeammate,
  iterateDatabaseAiTeammateDocumentParts,
  type DatabaseAiTeammateTraceContext
} from '@databases/ai-teammate-client';
import { logError } from '@databases/logs';
import { StreamingCsvParser, TokenType } from '@databases/streaming-csv-parser';
import { type DatabaseTextValue } from '@databases/types/database';
import { parseTextBasedFromHtml } from '@databases/utils/database/field-types/text';
import { parseHTML } from '@databases/utils/lib/dom';

import { readDatabaseContent } from './handlers/read-database-content-handler.ts';

export type PreviewDatabaseAiFieldOptions = {
  contentId: string;
  entryId: string;
  fieldName: string;
  locale: string;
  prompt: string;
  traceContext: DatabaseAiTeammateTraceContext;
  abortSignal?: AbortSignal;
};

function parsePreviewValue(value: string): DatabaseTextValue {
  const html = parseHTML(value);
  if (html.children().length === 0) {
    return { plain: value };
  }

  return parseTextBasedFromHtml(html);
}

export async function previewDatabaseAiField({
  contentId,
  entryId,
  fieldName,
  locale,
  prompt,
  traceContext,
  abortSignal
}: PreviewDatabaseAiFieldOptions): Promise<DatabaseTextValue | null> {
  const content = readDatabaseContent(contentId, {
    includeSelection: false,
    populateStreamMappings: false,
    entryScope: { kind: 'specific-entries', entryIds: [entryId] }
  });
  if (content.contentType !== 'database') {
    throw new Error('Expected database content for AI field preview');
  }

  const reader = await fetchDatabaseAiTeammate({
    input: {
      prompt,
      locale,
      databaseContext: {
        content_id: contentId,
        title: content.title,
        url: content.url,
        csv: content.csv
      }
    },
    traceContext,
    abortSignal
  });
  let summary: DatabaseTextValue | null = null;
  const parser = new StreamingCsvParser(
    (token) => {
      if (
        token.tokenType === TokenType.VALUE &&
        token.fieldName === fieldName &&
        typeof token.value === 'string'
      ) {
        summary = parsePreviewValue(token.value);
      }
    },
    'edit',
    {
      dataValueStrategy: {
        kind: 'single-target',
        targetFieldName: fieldName
      }
    }
  );

  try {
    for await (const documentPart of iterateDatabaseAiTeammateDocumentParts(reader)) {
      parser.onReceiveChunk(documentPart);
    }
    parser.onReceiveChunk('\n');

    const errors = parser.errorCollector.getErrors();
    if (errors.length > 0) {
      throw new Error(errors.map(({ message }) => message).join('; '));
    }

    return summary;
  } catch (error) {
    logError(
      'Failed to generate database AI field preview',
      error instanceof Error ? error : new Error(String(error))
    );

    try {
      await reader.cancel();
    } catch (cancelError) {
      logError(
        'Failed to cancel database AI field preview stream',
        cancelError instanceof Error ? cancelError : new Error(String(cancelError))
      );
    }

    throw error;
  }
}
